// Error codes from API
export const RESOURCENOTFOUND = 'GK.Resource.NotFound';
export const FIELDMISSING = 'GK.Field.Missing';
export const FIELDINVALID = 'GK.Field.Invalid';
export const FIELDUNEXPECTED = 'GK.Field.Unexpected';
export const PARAMSINVALID = 'GK.Params.Invalid';
export const HEADERMISSING = 'GK.Header.Missing';
export const HEADERINVALID = 'GK.Header.Invalid';
export const SERVERERROR = 'GK.Server.InternalError';
export const BADGATEWAY = 'GK.Server.BadGateway';
// Custom error class for API errors
export class ApiError extends Error {
    constructor(statusCode, errorCode, errorDescription, userMessage) {
        super(userMessage || errorDescription || 'An unexpected error occurred');
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.errorCode = errorCode;
        this.errorDescription = errorDescription;
        this.userMessage =
            userMessage || this.getDefaultUserMessage(statusCode, errorCode);
    }
    getDefaultUserMessage(statusCode, errorCode) {
        // Handle specific error codes
        if (errorCode) {
            switch (errorCode) {
                case RESOURCENOTFOUND:
                    return 'The requested resource was not found.';
                case FIELDMISSING:
                    return 'A required field is missing. Please check your input.';
                case FIELDINVALID:
                    return 'One or more fields contain invalid data. Please check your input.';
                case FIELDUNEXPECTED:
                    return 'Unexpected field provided. Please check your input.';
                case PARAMSINVALID:
                    return 'Invalid parameters provided. Please check your request.';
                case HEADERMISSING:
                case HEADERINVALID:
                    return 'Authentication error. Please try logging in again.';
                case SERVERERROR:
                    return 'A server error occurred. Please try again later.';
                case BADGATEWAY:
                    return 'Service temporarily unavailable. Please try again later.';
            }
        }
        // Handle by status code
        switch (statusCode) {
            case 400:
                return 'Invalid request. Please check your input and try again.';
            case 401:
                return 'Your session has expired. Please log in again.';
            case 403:
                return 'You do not have permission to perform this action.';
            case 404:
                return 'The requested resource was not found.';
            case 405:
                return 'This action is not allowed.';
            case 406:
                return 'The request format is not acceptable.';
            case 415:
                return 'The request format is not supported.';
            case 429:
                return 'Too many requests. Please wait a moment and try again.';
            case 500:
                return 'A server error occurred. Please try again later.';
            default:
                return 'An unexpected error occurred. Please try again.';
        }
    }
    isClientError() {
        return this.statusCode >= 400 && this.statusCode < 500;
    }
    isServerError() {
        return this.statusCode >= 500;
    }
    isAuthenticationError() {
        return (this.statusCode === 401 ||
            this.errorCode === HEADERMISSING ||
            this.errorCode === HEADERINVALID);
    }
    isAuthorizationError() {
        return this.statusCode === 403;
    }
    isNotFoundError() {
        return this.statusCode === 404 || this.errorCode === RESOURCENOTFOUND;
    }
    isValidationError() {
        return (this.statusCode === 400 &&
            (this.errorCode === FIELDMISSING ||
                this.errorCode === FIELDINVALID ||
                this.errorCode === FIELDUNEXPECTED ||
                this.errorCode === PARAMSINVALID));
    }
}
// Helper function to parse error response and create ApiError
export function parseApiError(statusCode, responseBody) {
    if (responseBody && typeof responseBody === 'object') {
        const errorCode = responseBody.errorCode;
        const errorDescription = responseBody.errorDescription;
        return new ApiError(statusCode, errorCode, errorDescription);
    }
    return new ApiError(statusCode);
}
// Helper function to handle common error scenarios
export function handleApiError(error) {
    // Log error for debugging
    console.error('API Error:', {
        statusCode: error.statusCode,
        errorCode: error.errorCode,
        errorDescription: error.errorDescription,
        userMessage: error.userMessage,
    });
    // Handle authentication errors by redirecting to login
    if (error.isAuthenticationError()) {
        // You might want to dispatch a Redux action here or use your auth service
        console.warn('Authentication error detected. User should be redirected to login.');
        // Example: window.location.href = '/login';
    }
    // Handle rate limiting
    if (error.statusCode === 429) {
        console.warn('Rate limit exceeded. Consider implementing exponential backoff.');
    }
    // You can add more specific handling here
}
